import React, { useState, useEffect, useContext, useMemo } from "react";
import { CreateButton, useTable } from "@refinedev/antd";
import {
  Input,
  Table,
  theme,
  type TableColumnsType,
  Form,
  Grid,
  Flex,
  Typography,
  Badge,
  Pagination,
  Button,
  Card,
} from "antd";
import { CrudFilters, HttpError, useNavigation } from "@refinedev/core";
import {
  PlusOutlined,
  LinkOutlined,
  ShopOutlined,
  SettingOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { PaginationTotal } from "@/components/pagination-total";
import { TFunction, i18n as I18nType } from "i18next";
import { useTranslation } from "react-i18next";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { EmptyFormCard } from "@/components";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

const { Title, Text } = Typography;
const { Search } = Input;

export const StoresList: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [startStore, setStartStore] = useState(true);
  const screens = Grid.useBreakpoint();
  const { setSmartMenuState, setSmartMenuName, setSmartMenuLabel, setSmartMenuType } =
    useContext(SmartMenuContext);

  const { token } = theme.useToken();
  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();
  const { push } = useNavigation();

  const onSearch = (params: any) => {
    const filters: CrudFilters = [];
    const { stores_search } = params;

    filters.push({
      field: "search",
      operator: "contains",
      value: stores_search,
    });

    return filters;
  };

  const {
    tableQuery: { data, isLoading, isFetching },
    searchFormProps,
    tableProps,
    filters,
    setFilters,
  } = useTable<HttpError>({
    resource: "stores",
    pagination: {
      pageSize: FILAS_POR_PAGINA,
    },
    sorters: {
      initial: [
        {
          field: "name",
          order: "asc",
        },
      ],
    },
    onSearch: onSearch,
  });

  const dataSource = data?.data || [];

  const columns: TableColumnsType<any> = [
    {
      key: "name",
      title: "Stores",
      dataIndex: "name",
      width: 400,
      sorter: (a, b) =>
        a.name.toLowerCase().charCodeAt(0) - b.name.toLowerCase().charCodeAt(0),
      render: (_, record) => {
        return (
          <Flex
            gap={7}
            justify="space-between"
            align="center"
            style={{ minWidth: 200 }}
          >
            <div>
              <ShopOutlined style={{ marginRight: 6 }} />
              <Text strong>{record.name}</Text>
            </div>
            <Button
              onClick={(e) => {
                e.stopPropagation();
              }}
              shape="circle"
              icon={<LinkOutlined />}
              href={record.link}
              target="_blank"
            />
          </Flex>
        );
      },
    },
    {
      key: "catalogProducts",
      title: t("contacts.tag.products.in.catalog"),
      dataIndex: "catalogProducts",
      sorter: (a, b) => a.catalogProducts - b.catalogProducts,
    },
    {
      key: "linkedSellers",
      title: t("pages.linked sellers.title"),
      dataIndex: "linkedSellers",
      sorter: (a, b) => a.linkedSellers - b.linkedSellers,
    },
    {
      key: "status",
      title: t("posts.fields.status.title"),
      dataIndex: "status",
      sorter: (a, b) =>
        a.status.toLowerCase().charCodeAt(0) -
        b.status.toLowerCase().charCodeAt(0),
      render: (status) => {
        return (
          <Flex gap={7}>
            <Badge status={status === "active" ? "success" : "warning"} />{" "}
            <span style={{ textTransform: "capitalize" }}>{status}</span>
          </Flex>
        );
      },
    },
  ];

  useEffect(() => {
    if (dataSource) {
      setStartStore(false);
    }
  }, [dataSource]);

  const HeaderRow = React.memo(() => (
    <>
      <Flex flex="block" gap="large" wrap={screens.sm ? false : true}>
        <Form {...searchFormProps} style={{ width: "100%" }}>
          <Form.Item name="stores_search">
            <Search
              placeholder={"Buscar store"}
              allowClear
              onSearch={(value) => {
                if (value.trim() === "") {
                  searchFormProps.form?.setFieldsValue({
                    sellers_search: undefined,
                  });
                }
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
        </Form>
        <Flex
          gap="large"
          justify="center"
          style={{ width: screens.sm ? "auto" : "100%" }}
        >
          <CreateButton
            type="primary"
            icon={<PlusOutlined />}
            iconPosition="end"
          >
            {t("buttom.new.store")}
          </CreateButton>
        </Flex>
      </Flex>
    </>
  ));

  const handleRowClick = (record: HttpError) => {
    push(`linked-sellers`);
    setSmartMenuName(record.code);
    // El encabezado del menú mostraba el CÓDIGO capitalizado (`Comercialcmp`). El código es lo que
    // va en la URL; el nombre es lo que lee una persona.
    setSmartMenuLabel((record as unknown as { name?: string }).name ?? record.code);
    setSmartMenuType("stores");
    setSmartMenuState(true);
  };

  const HeaderContent = React.memo(() => (
    <>
      <Flex justify="space-between" style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 0 }}>
          <ShopOutlined /> {t("menu.label.stores")}
        </Title>
      </Flex>
    </>
  ));

  if (startStore) {
    return (
      <>
        <HeaderContent />
        <EmptyFormCard
          message="Get started by creating your store and begin selling today."
          loading={isFetching}
          icon={<ShopOutlined />}
        >
          <CreateButton
            type="primary"
            icon={<PlusOutlined />}
            iconPosition="end"
          >
            {t("buttom.new.store")}
          </CreateButton>
        </EmptyFormCard>
      </>
    );
  }

  return (
    <>
      <HeaderContent />
      <Table
        {...tableProps}
        scroll={{ x: "max-content", y: 55 * 10.5 }}
        style={{
          cursor: "pointer",
        }}
        title={() => <HeaderRow />}
        onRow={(record) => {
          return {
            onClick: () => {
              handleRowClick(record);
            },
          };
        }}
        rowKey="store_id"
        dataSource={dataSource}
        columns={columns}
        loading={isFetching}
        locale={{
          triggerAsc: t("table.triggerAsc"),
          triggerDesc: t("table.triggerDesc"),
          cancelSort: t("table.cancelSort"),
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: OPCIONES_POR_PAGINA,
          locale: {
            items_per_page: t("table.itemsPerPage"),
            prev_page: t("table.prevPage"),
            next_page: t("table.nextPage"),
            jump_to: t("table.jumpTo"),
            jump_to_confirm: t("table.jumpToConfirm"),
            page: t("table.page"),
            next_5: t("table.next5"),
            prev_5: t("table.prev5"),
          },
          showTotal: (total) => (
            <PaginationTotal total={total} entityName="stores" />
          ),
        }}
      />
      {children}
    </>
  );
};
